/** The activity's **Documents & links** panel (plan §3 "Documents & links", D5, O12) — an
 *  IMMEDIATE panel, like the gallery: every write is saved at once on the server (and audited
 *  there with its values), never part of the record's draft, so it reads and works the same
 *  in view and edit mode and pressing E moves nothing (Zero-Re-Layout).
 *
 *  - Header: "Documents & links" · "Saved immediately · inherited by N tour stops" (the bolt
 *    of an immediate action; the tour stops whose documents are "Use activity documents").
 *  - Rows, in the app's order: the kind's icon, the name (a link that opens the file or the
 *    address in a new tab), its facts (PDF / the link's host, "not downloaded yet", "opens in
 *    the app", "360°"); for a manager, **Move up now** / **Move down now**, **Rename now**
 *    (the name in place: Enter saves, Esc cancels) and **Remove now…** (an anchored confirm,
 *    focus on Cancel).
 *  - Foot, for a manager: **Upload file now** (at most 32 MB — a larger file is refused before
 *    it is sent) and **Add link now** (an address, and a name that defaults to it).
 *  - Refusals in the server's words (`catalogWriteErrors`: the field a refusal names, the
 *    upload's 413) where the action was taken; a reorder that fails goes back to the stored
 *    order and says so.
 *  - Who may write: `console_activities_manage` (a viewer sees the list only — no controls);
 *    an activity without an operator is refused by the server, so it shows no controls and
 *    says why. The catalog edit switch does not gate it (plan A10b-1: documents, duplicate and
 *    the galleries stay outside it), and the server does not either.
 *  - The list is asked for once the panel has been on screen; while it loads, the detail's
 *    count of rows stands where they will land. */
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import LinkIcon from '@mui/icons-material/Link';
import {
  Alert,
  Box,
  Button,
  IconButton,
  LinearProgress,
  Link,
  Popover,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useId, useRef, useState, type FormEvent, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';

import {
  useActivityDocuments,
  useActivityDocumentWrites,
  type ActivityDocument,
} from '@/api/activityDocuments';
import { SectionCard } from '@/components/ReadView';
import { useSnackbar } from '@/components/Snackbar';
import { IMMEDIATE_DESCRIPTION, ImmediateActionButton } from '@/features/records/actions/ImmediateActionButton';
import { RecordCardSkeleton } from '@/features/records/page/RecordStates';
import { useSeenOnce } from '@/features/records/page/useSeenOnce';

import { documentErrorText, documentFacts, inheritedText, removeText, tooLargeText } from './documentsModel';

export const DOCUMENTS_ANCHOR_ID = 'activity-section-documents';
export const DOCUMENTS_TITLE = 'Documents & links';
/** The rows' list, inside the panel's region. */
export const DOCUMENTS_LIST_LABEL = 'Documents';
export const DOCUMENTS_NO_OPERATOR = 'No owning operator: its documents can’t be changed here.';
const EMPTY = 'No documents or links yet.';

/** An icon button whose action happens at once (not part of the draft). */
const IMMEDIATE = { 'aria-description': IMMEDIATE_DESCRIPTION } as Record<string, string>;

const LIST_SX = { listStyle: 'none', m: 0, p: 0 } as const;
/** One row: the kind's icon, the name over its facts, the actions. */
const ROW_SX = {
  display: 'grid',
  gridTemplateColumns: '20px minmax(0, 1fr) auto',
  columnGap: 1,
  alignItems: 'center',
  minHeight: 48,
  py: 0.5,
  borderTop: 1,
  borderColor: 'divider',
  '&:first-of-type': { borderTop: 0 },
} as const;
const ICON_SX = { fontSize: 18, color: 'text.secondary' } as const;
const ROW_SKELETON_WIDTHS = ['64%', '48%', '56%', '40%'];

export interface DocumentsPanelProps {
  activityId: number;
  /** The detail's count: the rows' room while the list loads. */
  documentCount: number;
  /** `console_activities_manage`: without it, the list only. */
  canManage: boolean;
  /** The activity has no operator: the server refuses its writes. */
  orphan: boolean;
}

export function DocumentsPanel({ activityId, documentCount, canManage, orphan }: DocumentsPanelProps) {
  const [seenRef, seen] = useSeenOnce<HTMLElement>();
  const listQ = useActivityDocuments(activityId, { enabled: seen });
  const writes = useActivityDocumentWrites(activityId);
  const snackbar = useSnackbar();
  const canWrite = canManage && !orphan;
  const list = listQ.data;
  const docs = list?.results ?? [];

  const [renaming, setRenaming] = useState<number | null>(null);
  const [removing, setRemoving] = useState<{ doc: ActivityDocument; anchor: HTMLElement } | null>(null);
  const busy =
    writes.upload.isPending ||
    writes.addLink.isPending ||
    writes.rename.isPending ||
    writes.remove.isPending ||
    writes.reorder.isPending;

  const move = (index: number, delta: number) => {
    const to = index + delta;
    if (to < 0 || to >= docs.length) return;
    const ids = docs.map((d) => d.id);
    [ids[index], ids[to]] = [ids[to]!, ids[index]!];
    writes.reorder.mutate(ids, {
      onError: (e) => snackbar.show(`The order was not changed. ${documentErrorText(e, '')}`.trim(), 'error'),
    });
  };

  let body: ReactNode;
  if (!list) {
    body = listQ.error ? (
      <Box role="alert" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }} data-documents-error>
        <ErrorOutlineIcon aria-hidden sx={{ fontSize: 16, color: 'error.main' }} />
        <Typography variant="body2" component="span">
          Couldn’t load the documents.
        </Typography>
        <Button size="small" onClick={() => void listQ.refetch()} sx={{ minWidth: 0, py: 0 }}>
          Retry
        </Button>
      </Box>
    ) : (
      <DocumentRowsSkeleton rows={documentCount} />
    );
  } else if (docs.length === 0) {
    body = (
      <Typography variant="body2" color="text.secondary" data-documents-empty>
        {EMPTY}
      </Typography>
    );
  } else {
    body = (
      // Named apart from its section ("Documents & links"): a list inside a region of the same
      // name reads twice.
      <Box component="ol" sx={LIST_SX} aria-label={DOCUMENTS_LIST_LABEL}>
        {docs.map((doc, i) => (
          <DocumentRow
            key={doc.id}
            doc={doc}
            first={i === 0}
            last={i === docs.length - 1}
            canWrite={canWrite}
            busy={busy}
            renaming={renaming === doc.id}
            onRename={() => setRenaming(doc.id)}
            onRenameDone={() => setRenaming(null)}
            rename={(name) => writes.rename.mutateAsync({ docId: doc.id, name })}
            onMove={(delta) => move(i, delta)}
            onRemove={(anchor) => setRemoving({ doc, anchor })}
          />
        ))}
      </Box>
    );
  }

  return (
    <Box
      component="section"
      ref={seenRef}
      id={DOCUMENTS_ANCHOR_ID}
      tabIndex={-1}
      aria-label={DOCUMENTS_TITLE}
      data-activity-documents
      sx={{
        outline: 'none',
        borderRadius: 1,
        '&:focus-visible': { boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}` },
        // The cards' column spaces the panels (RecordPanel's rule).
        '& > .MuiCard-root': { mb: 0 },
      }}
    >
      <SectionCard title={DOCUMENTS_TITLE} action={<SavedImmediately inherited={list?.inherited_by ?? null} />}>
        <Box sx={{ display: 'grid', gap: 1 }}>
          {body}
          {canWrite && (
            <AddDocument
              busy={busy}
              upload={(file) => writes.upload.mutateAsync({ file })}
              addLink={(url, name) => writes.addLink.mutateAsync({ url, ...(name ? { name } : {}) })}
              onAdded={(name) => snackbar.show(`“${name}” added — the app shows it now.`, 'success')}
            />
          )}
          {canManage && orphan && (
            <Typography variant="caption" color="text.secondary" data-documents-orphan>
              {DOCUMENTS_NO_OPERATOR}
            </Typography>
          )}
        </Box>
      </SectionCard>
      <RemoveConfirm
        target={removing}
        inherited={list?.inherited_by ?? 0}
        remove={(doc) => writes.remove.mutateAsync(doc.id)}
        onClose={() => setRemoving(null)}
        onRemoved={(doc) => {
          setRemoving(null);
          snackbar.show(`“${doc.name}” removed — the app no longer shows it.`, 'success');
        }}
      />
    </Box>
  );
}

/** "Saved immediately · inherited by 3 tour stops", with the immediate action's bolt. */
function SavedImmediately({ inherited }: { inherited: number | null }) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, textAlign: 'right' }}
      data-documents-caption
    >
      <BoltOutlinedIcon aria-hidden sx={{ fontSize: 14 }} />
      {inherited === null ? 'Saved immediately' : `Saved immediately · ${inheritedText(inherited)}`}
    </Typography>
  );
}

function DocumentRow({
  doc,
  first,
  last,
  canWrite,
  busy,
  renaming,
  onRename,
  onRenameDone,
  rename,
  onMove,
  onRemove,
}: {
  doc: ActivityDocument;
  first: boolean;
  last: boolean;
  canWrite: boolean;
  busy: boolean;
  renaming: boolean;
  onRename: () => void;
  onRenameDone: () => void;
  rename: (name: string) => Promise<unknown>;
  onMove: (delta: number) => void;
  onRemove: (anchor: HTMLElement) => void;
}) {
  const icon = doc.type === 'link' ? <LinkIcon aria-hidden sx={ICON_SX} /> : <InsertDriveFileOutlinedIcon aria-hidden sx={ICON_SX} />;
  const name = doc.link ? (
    <Link href={doc.link} target="_blank" rel="noopener noreferrer" underline="hover" variant="body2" noWrap sx={{ display: 'block' }}>
      {doc.name}
    </Link>
  ) : (
    <Typography variant="body2" noWrap>
      {doc.name}
    </Typography>
  );
  return (
    <Box component="li" sx={ROW_SX} data-document={doc.id}>
      {icon}
      {renaming ? (
        <RenameField doc={doc} rename={rename} onDone={onRenameDone} />
      ) : (
        <Box sx={{ minWidth: 0 }} title={doc.name}>
          {name}
          <Typography variant="caption" color="text.secondary" noWrap component="div" data-document-facts>
            {documentFacts(doc).join(' · ')}
          </Typography>
        </Box>
      )}
      {canWrite && !renaming ? (
        <Box sx={{ display: 'flex' }}>
          <RowAction label={`Move “${doc.name}” up now`} tip="Move up now" disabled={busy || first} onClick={() => onMove(-1)}>
            <ArrowUpwardIcon fontSize="small" />
          </RowAction>
          <RowAction label={`Move “${doc.name}” down now`} tip="Move down now" disabled={busy || last} onClick={() => onMove(1)}>
            <ArrowDownwardIcon fontSize="small" />
          </RowAction>
          <RowAction label={`Rename “${doc.name}” now`} tip="Rename now" disabled={busy} onClick={onRename}>
            <EditOutlinedIcon fontSize="small" />
          </RowAction>
          <RowAction
            label={`Remove “${doc.name}” now…`}
            tip="Remove now…"
            disabled={busy}
            danger
            onClick={(e) => onRemove(e.currentTarget)}
          >
            <DeleteOutlineIcon fontSize="small" />
          </RowAction>
        </Box>
      ) : (
        <span />
      )}
    </Box>
  );
}

function RowAction({
  label,
  tip,
  disabled,
  danger = false,
  onClick,
  children,
}: {
  label: string;
  tip: string;
  disabled: boolean;
  danger?: boolean;
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
  children: ReactNode;
}) {
  return (
    <Tooltip title={tip}>
      <span>
        <IconButton
          size="small"
          aria-label={label}
          disabled={disabled}
          onClick={onClick}
          {...(danger ? { color: 'error' as const } : {})}
          {...IMMEDIATE}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

/** The name in place: Enter (or Rename now) saves it at once, Esc (or Cancel) keeps it. */
function RenameField({
  doc,
  rename,
  onDone,
}: {
  doc: ActivityDocument;
  rename: (name: string) => Promise<unknown>;
  onDone: () => void;
}) {
  const [value, setValue] = useState(doc.name);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const trimmed = value.trim();
  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (!trimmed || saving) return;
    if (trimmed === doc.name) {
      onDone();
      return;
    }
    setSaving(true);
    setError(null);
    rename(trimmed).then(onDone, (err: unknown) => {
      setSaving(false);
      setError(documentErrorText(err, 'The name was not changed.'));
    });
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onDone();
    }
  };
  return (
    <Box
      component="form"
      onSubmit={submit}
      sx={{ gridColumn: '2 / -1', display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap', py: 0.5 }}
      data-document-rename
    >
      <TextField
        size="small"
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={onKeyDown}
        error={Boolean(error) || !trimmed}
        helperText={error ?? (!trimmed ? 'A name is needed.' : undefined)}
        inputProps={{ 'aria-label': `Name of “${doc.name}”`, maxLength: 256 }}
        sx={{ flex: '1 1 200px', minWidth: 0 }}
      />
      <ImmediateActionButton type="submit" variant="contained" disabled={!trimmed || saving}>
        Rename now
      </ImmediateActionButton>
      <Button size="small" onClick={onDone} disabled={saving}>
        Cancel
      </Button>
    </Box>
  );
}

/** Upload file now · Add link now, and the link's form. */
function AddDocument({
  busy,
  upload,
  addLink,
  onAdded,
}: {
  busy: boolean;
  upload: (file: File) => Promise<ActivityDocument>;
  addLink: (url: string, name: string) => Promise<ActivityDocument>;
  onAdded: (name: string) => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const urlId = useId();
  const [uploading, setUploading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [linkForm, setLinkForm] = useState(false);
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [adding, setAdding] = useState(false);

  const pick = (file: File | undefined) => {
    if (!file) return;
    const big = tooLargeText(file);
    if (big) {
      setError(big);
      return;
    }
    setError(null);
    setUploading(file.name);
    upload(file).then(
      (doc) => {
        setUploading(null);
        onAdded(doc.name);
      },
      (e: unknown) => {
        setUploading(null);
        setError(documentErrorText(e, `“${file.name}” was not uploaded.`));
      },
    );
  };

  const submitLink = (e: FormEvent) => {
    e.preventDefault();
    const address = url.trim();
    if (!address || adding) return;
    setAdding(true);
    setError(null);
    addLink(address, name.trim()).then(
      (doc) => {
        setAdding(false);
        setLinkForm(false);
        setUrl('');
        setName('');
        onAdded(doc.name);
      },
      (err: unknown) => {
        setAdding(false);
        setError(documentErrorText(err, 'The link was not added.'));
      },
    );
  };

  return (
    <Box sx={{ display: 'grid', gap: 1, pt: 0.5 }} data-documents-add>
      {linkForm && (
        <Box
          component="form"
          onSubmit={submitLink}
          aria-label="Add a link"
          sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}
        >
          <TextField
            id={urlId}
            size="small"
            label="Address"
            type="url"
            autoFocus
            required
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://"
            inputProps={{ maxLength: 2048 }}
            sx={{ flex: '2 1 220px', minWidth: 0 }}
          />
          <TextField
            size="small"
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="The address"
            inputProps={{ maxLength: 256 }}
            sx={{ flex: '1 1 160px', minWidth: 0 }}
          />
          <ImmediateActionButton type="submit" variant="contained" disabled={!url.trim() || adding}>
            Add link now
          </ImmediateActionButton>
          <Button
            size="small"
            disabled={adding}
            onClick={() => {
              setLinkForm(false);
              setError(null);
            }}
          >
            Cancel
          </Button>
        </Box>
      )}
      {uploading && (
        <Box role="status" sx={{ display: 'grid', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Uploading “{uploading}”…
          </Typography>
          <LinearProgress aria-label={`Uploading ${uploading}`} sx={{ height: 2 }} />
        </Box>
      )}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ py: 0, '& .MuiAlert-message': { typography: 'body2', py: 0.75 } }}
          data-documents-add-error
        >
          {error}
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <ImmediateActionButton
          variant="outlined"
          disabled={busy || uploading !== null}
          onClick={() => fileInput.current?.click()}
        >
          Upload file now
        </ImmediateActionButton>
        {!linkForm && (
          <ImmediateActionButton
            variant="outlined"
            disabled={busy}
            onClick={() => {
              setLinkForm(true);
              setError(null);
            }}
          >
            Add link now
          </ImmediateActionButton>
        )}
        <input
          ref={fileInput}
          hidden
          type="file"
          data-document-input
          onChange={(e) => {
            pick(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
      </Box>
    </Box>
  );
}

/** Remove now…: an anchored confirm (`role="alertdialog"`, focus on Cancel); a refusal stays
 *  in it with Retry. */
function RemoveConfirm({
  target,
  inherited,
  remove,
  onClose,
  onRemoved,
}: {
  target: { doc: ActivityDocument; anchor: HTMLElement } | null;
  inherited: number;
  remove: (doc: ActivityDocument) => Promise<unknown>;
  onClose: () => void;
  onRemoved: (doc: ActivityDocument) => void;
}) {
  const titleId = useId();
  const bodyId = useId();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const doc = target?.doc ?? null;
  const close = () => {
    if (pending) return;
    setError(null);
    onClose();
  };
  const confirm = () => {
    if (!doc) return;
    setPending(true);
    setError(null);
    remove(doc).then(
      () => {
        setPending(false);
        onRemoved(doc);
      },
      (e: unknown) => {
        setPending(false);
        setError(documentErrorText(e, 'It was not removed.'));
      },
    );
  };
  return (
    <Popover
      open={Boolean(target)}
      anchorEl={target?.anchor ?? null}
      onClose={close}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          role: 'alertdialog',
          'aria-labelledby': titleId,
          'aria-describedby': bodyId,
          'data-document-remove': '',
          sx: { p: 1.5, width: 340, maxWidth: 'calc(100vw - 32px)', display: 'grid', gap: 1 },
        } as object,
      }}
    >
      {doc && (
        <>
          <Typography id={titleId} variant="subtitle2" component="h2">
            Remove “{doc.name}” now?
          </Typography>
          <Typography id={bodyId} variant="body2">
            {removeText(inherited)}
          </Typography>
          {error && (
            <Alert severity="error" sx={{ py: 0, '& .MuiAlert-message': { typography: 'body2', py: 0.75 } }}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
            <Button size="small" autoFocus onClick={close} disabled={pending}>
              Cancel
            </Button>
            <ImmediateActionButton variant="contained" color="error" onClick={confirm} disabled={pending}>
              {error ? 'Retry' : 'Remove now'}
            </ImmediateActionButton>
          </Box>
        </>
      )}
    </Popover>
  );
}

/** The rows while the list loads: the detail's count of them (one line when it has none). */
function DocumentRowsSkeleton({ rows }: { rows: number }) {
  if (rows <= 0) {
    return (
      <Typography variant="body2" component="div" data-documents-loading>
        <Skeleton width="40%" />
      </Typography>
    );
  }
  return (
    <Box role="status" aria-label="Loading the documents" sx={LIST_SX} data-documents-loading>
      {Array.from({ length: rows }, (_, i) => (
        <Box key={i} sx={ROW_SX}>
          <Skeleton variant="rounded" width={18} height={18} />
          <Box>
            <Typography variant="body2" component="div">
              <Skeleton width={ROW_SKELETON_WIDTHS[i % ROW_SKELETON_WIDTHS.length]} />
            </Typography>
            <Typography variant="caption" component="div">
              <Skeleton width="24%" />
            </Typography>
          </Box>
          <span />
        </Box>
      ))}
    </Box>
  );
}

/** The panel while the record page loads (no detail yet): its card and the caption's room. */
export function DocumentsPanelSkeleton() {
  return (
    <RecordCardSkeleton
      action={
        <Typography variant="caption" component="div">
          <Skeleton width={180} />
        </Typography>
      }
    >
      <DocumentRowsSkeleton rows={2} />
    </RecordCardSkeleton>
  );
}
